import { type ReactNode, type VideoHTMLAttributes, type AudioHTMLAttributes, useEffect, useRef } from 'react';
import { Loader } from '@lms/ui';
import { useAuthenticatedBlobUrl } from '@/hooks/useAuthenticatedBlobUrl';
import { SafeHtml } from '@/components/SafeHtml';
import { PdfDocumentViewer } from '@/components/learning/PdfDocumentViewer';
import { PdfFolderPlayer } from '@/components/learning/PdfFolderPlayer';
import { useLearningSessionContext } from '@/providers/learning-session-context';

function TrackedVideo(props: VideoHTMLAttributes<HTMLVideoElement>) {
  const session = useLearningSessionContext();
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !session) return;
    const onPlay = () => {
      session.setMediaPlaying(true);
      session.markActivity();
    };
    const onStop = () => session.setMediaPlaying(false);
    el.addEventListener('playing', onPlay);
    el.addEventListener('ended', onStop);
    return () => {
      el.removeEventListener('playing', onPlay);
      el.removeEventListener('ended', onStop);
      session.setMediaPlaying(false);
    };
  }, [session]);

  return <video ref={ref} {...props} />;
}

function TrackedEmbedVideo({ src, title }: { src: string; title: string }) {
  const session = useLearningSessionContext();

  useEffect(() => {
    if (!session) return;
    session.setMediaPlaying(true);
    session.markActivity();
    return () => session.setMediaPlaying(false);
  }, [session, src]);

  return (
    <div className="aspect-video overflow-hidden rounded-xl border border-dash-outline-variant bg-black">
      <iframe
        src={src}
        title={title}
        className="h-full w-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function TrackedAudio(props: AudioHTMLAttributes<HTMLAudioElement>) {
  const session = useLearningSessionContext();
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !session) return;
    const onPlay = () => {
      session.setMediaPlaying(true);
      session.markActivity();
    };
    const onStop = () => session.setMediaPlaying(false);
    el.addEventListener('playing', onPlay);
    el.addEventListener('ended', onStop);
    return () => {
      el.removeEventListener('playing', onPlay);
      el.removeEventListener('ended', onStop);
      session.setMediaPlaying(false);
    };
  }, [session]);

  return <audio ref={ref} {...props} />;
}

function AuthenticatedPdf({ mediaUrl, title }: { mediaUrl: string; title: string }) {
  const { blob, blobUrl, error, loading } = useAuthenticatedBlobUrl(mediaUrl);
  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader label="Cargando contenido…" />
      </div>
    );
  }
  if (error || !blob || !blobUrl) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
        {error ?? 'No se pudo cargar el archivo.'}
      </div>
    );
  }
  return <PdfDocumentViewer file={blob} downloadUrl={blobUrl} title={title} />;
}

export interface LearningObjectPlayContent {
  type: string;
  title: string;
  html?: string;
  externalUrl?: string;
  mediaUrl?: string;
  mimeType?: string;
  download?: boolean;
  assessmentId?: string;
  assessmentInstanceId?: string;
  files?: { mediaFileId: string; title: string; mediaUrl: string }[];
}

function isEmbeddableVideo(url: string) {
  return /youtube\.com|youtu\.be|vimeo\.com|panopto\.com/i.test(url);
}

function embedVideoUrl(url: string) {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) {
      const id = parsed.pathname.replace('/', '');
      return id ? `https://www.youtube.com/embed/${id}` : url;
    }
    if (parsed.hostname.includes('youtube.com')) {
      const id = parsed.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : url;
    }
    if (parsed.hostname.includes('vimeo.com')) {
      const id = parsed.pathname.split('/').filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}` : url;
    }
  } catch {
    return url;
  }
  return url;
}

function AuthenticatedMedia({
  mediaUrl,
  render,
}: {
  mediaUrl: string;
  render: (src: string) => ReactNode;
}) {
  const { blobUrl, error, loading } = useAuthenticatedBlobUrl(mediaUrl);
  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader label="Cargando contenido…" />
      </div>
    );
  }
  if (error || !blobUrl) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
        {error ?? 'No se pudo cargar el archivo.'}
      </div>
    );
  }
  return <>{render(blobUrl)}</>;
}

export function LearningObjectPlayer({ content }: { content: LearningObjectPlayContent }) {
  const { type, html, externalUrl, mediaUrl, mimeType, download } = content;
  const mediaSrc = mediaUrl?.startsWith('/') ? mediaUrl : mediaUrl;
  const external = externalUrl?.trim();

  if (type === 'video') {
    if (external && isEmbeddableVideo(external)) {
      return <TrackedEmbedVideo src={embedVideoUrl(external)} title={content.title} />;
    }
    if (mediaSrc) {
      return (
        <AuthenticatedMedia
          mediaUrl={mediaSrc}
          render={(src) => (
            <TrackedVideo controls className="w-full rounded-xl border border-dash-outline-variant bg-black" src={src}>
              <track kind="captions" />
            </TrackedVideo>
          )}
        />
      );
    }
    if (external) {
      return (
        <TrackedVideo controls className="w-full rounded-xl border border-dash-outline-variant bg-black" src={external}>
          <track kind="captions" />
        </TrackedVideo>
      );
    }
  }

  if (type === 'audio' && mediaSrc) {
    return (
      <AuthenticatedMedia
        mediaUrl={mediaSrc}
        render={(src) => (
          <TrackedAudio controls className="w-full" src={src}>
            <track kind="captions" />
          </TrackedAudio>
        )}
      />
    );
  }

  if (type === 'audio' && external) {
    return (
      <TrackedAudio controls className="w-full" src={external}>
        <track kind="captions" />
      </TrackedAudio>
    );
  }

  if (type === 'pdf' && mediaSrc) {
    return <AuthenticatedPdf mediaUrl={mediaSrc} title={content.title} />;
  }

  if (type === 'pdf_folder') {
    return <PdfFolderPlayer files={content.files ?? []} />;
  }

  if (type === 'image' && mediaSrc) {
    return (
      <AuthenticatedMedia
        mediaUrl={mediaSrc}
        render={(src) => (
          <img
            src={src}
            alt={content.title}
            className="mx-auto max-h-[75vh] max-w-full rounded-xl border border-dash-outline-variant object-contain"
          />
        )}
      />
    );
  }

  if (type === 'image' && external) {
    return (
      <img
        src={external}
        alt={content.title}
        className="mx-auto max-h-[75vh] max-w-full rounded-xl border border-dash-outline-variant object-contain"
      />
    );
  }

  if ((type === 'rich_text' || type === 'html') && html) {
    return (
      <SafeHtml
        as="article"
        html={html}
        className="prose prose-sm max-w-none rounded-xl border border-dash-outline-variant bg-white p-6 md:prose-base"
      />
    );
  }

  if (type === 'external_link' && external) {
    return (
      <div className="rounded-xl border border-dash-outline-variant bg-white p-8 text-center">
        <p className="mb-4 text-dash-on-surface-variant">Enlace externo</p>
        <a
          href={external}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white hover:opacity-90"
        >
          Abrir enlace
          <span className="material-symbols-outlined text-base" aria-hidden="true">
            open_in_new
          </span>
        </a>
      </div>
    );
  }

  if ((type === 'download' || download) && mediaSrc) {
    return (
      <AuthenticatedMedia
        mediaUrl={mediaSrc}
        render={(src) => (
          <div className="rounded-xl border border-dash-outline-variant bg-white p-8 text-center">
            <span className="material-symbols-outlined mb-4 text-5xl text-dash-secondary" aria-hidden="true">
              download
            </span>
            <p className="mb-4 text-dash-on-surface-variant">Archivo descargable</p>
            <a
              href={src}
              download
              className="inline-flex items-center gap-2 rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white hover:opacity-90"
            >
              Descargar
            </a>
          </div>
        )}
      />
    );
  }

  if (type === 'assessment') {
    return (
      <div className="rounded-xl border border-dash-outline-variant bg-white p-8 text-center">
        <span className="material-symbols-outlined mb-4 text-5xl text-dash-secondary" aria-hidden="true">
          quiz
        </span>
        <p className="text-dash-on-surface-variant">
          Evaluación — inicia el intento desde la vista de estudiante inscrito en el curso.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-dash-outline-variant bg-white p-8 text-center text-sm text-dash-on-surface-variant">
      Contenido no disponible para el tipo «{type}»{mimeType ? ` (${mimeType})` : ''}.
    </div>
  );
}
